const UTF8Decoder = typeof TextDecoder != 'undefined' ? new TextDecoder() : undefined;

/**
 * Given a pointer 'idx' to a null-terminated UTF8-encoded string in the given array that contains uint8 values,
 * returns a copy of that string as a Javascript String object.
 *
 * heapOrArray is either a regular array, or a JavaScript typed array view.
 * @param heapOrArray
 * @param {number} idx
 * @param {number=} maxBytesToRead
 * @return {string}
 */
export function UTF8ArrayToString(heapOrArray, idx, maxBytesToRead) {
  const endIdx = idx + maxBytesToRead;
  let endPtr = idx;

  // TextDecoder needs to know the byte length in advance,
  // it doesn't stop on null terminator by itself.
  // Also, use the length info to avoid running tiny strings through TextDecoder,
  // since .subarray() allocates garbage.
  // (As a tiny code save trick, compare endPtr against endIdx using a negation, so that undefined means Infinity)
  while (heapOrArray[endPtr] && !(endPtr >= endIdx)) {
    ++endPtr;
  }

  if (endPtr - idx > 16 && heapOrArray.buffer && UTF8Decoder) {
    return UTF8Decoder.decode(heapOrArray.subarray(idx, endPtr));
  }

  let str = '';
  // If building with TextDecoder, we have already computed the string length above, so test loop end condition against that
  while (idx < endPtr) {
    // For UTF8 byte structure, see:
    // http://en.wikipedia.org/wiki/UTF-8#Description
    // https://www.ietf.org/rfc/rfc2279.txt
    // https://tools.ietf.org/html/rfc3629
    let u0 = heapOrArray[idx++];
    if (!(u0 & 0x80)) {
      str += String.fromCharCode(u0);
      continue;
    }

    const u1 = heapOrArray[idx++] & 63;
    if ((u0 & 0xE0) === 0xC0) {
      str += String.fromCharCode((u0 & 31) << 6 | u1);
      continue;
    }

    const u2 = heapOrArray[idx++] & 63;
    if ((u0 & 0xF0) === 0xE0) {
      u0 = (u0 & 15) << 12 | u1 << 6 | u2;
    } else {
      if ((u0 & 0xF8) !== 0xF0) warnOnce(`Invalid UTF-8 leading byte ${ptrToString(u0)} encountered when deserializing a UTF-8 string in wasm memory to a JS string!`);
      u0 = (u0 & 7) << 18 | u1 << 12 | u2 << 6 | heapOrArray[idx++] & 63;
    }

    if (u0 < 0x10000) {
      str += String.fromCharCode(u0);
    } else {
      const ch = u0 - 0x10000;
      str += String.fromCharCode(0xD800 | ch >> 10, 0xDC00 | ch & 0x3FF);
    }
  }

  return str;
}

export function lengthBytesUTF8(str) {
  let len = 0;
  for (let i = 0; i < str.length; ++i) {
    // Gotcha: charCodeAt returns a 16-bit word that is a UTF-16 encoded code unit, not a Unicode code point of the character!
    // So decode UTF16->UTF32->UTF8.
    // See http://unicode.org/faq/utf_bom.html#utf16-3
    const c = str.charCodeAt(i); // possibly a lead surrogate
    if (c <= 0x7F) {
      len++;
    } else if (c <= 0x7FF) {
      len += 2;
    } else if (c >= 0xD800 && c <= 0xDFFF) {
      len += 4;
      ++i;
    } else {
      len += 3;
    }
  }

  return len;
}

export function ptrToString(ptr) {
  // With CAN_ADDRESS_2GB or MEMORY64, pointers are already unsigned.
  ptr >>>= 0;
  return `0x${ptr.toString(16).padStart(8, '0')}`;
}

export function warnOnce(text) {
  warnOnce.shown || (warnOnce.shown = {});

  if (!warnOnce.shown[text]) {
    warnOnce.shown[text] = 1;
    console.error(new Error(text));
  }
}

export function stringToUTF8Array(str, heap, outIdx, maxBytesToWrite) {
  // Parameter maxBytesToWrite is not optional.
  // Negative values, 0, null, undefined and false each don't write out any bytes.
  if (!(maxBytesToWrite > 0)) return 0;
  const startIdx = outIdx;
  const endIdx = outIdx + maxBytesToWrite - 1; // -1 for string null terminator.
  for (let i = 0; i < str.length; ++i) {
    // Gotcha: charCodeAt returns a 16-bit word that is a UTF-16 encoded code unit, not a Unicode code point of the character!
    // So decode UTF16->UTF32->UTF8.
    // See http://unicode.org/faq/utf_bom.html#utf16-3
    // For UTF8 byte structure, see http://en.wikipedia.org/wiki/UTF-8#Description
    // and https://www.ietf.org/rfc/rfc2279.txt
    // and https://tools.ietf.org/html/rfc3629
    let u = str.charCodeAt(i); // possibly a lead surrogate
    if (u >= 0xD800 && u <= 0xDFFF) {
      const u1 = str.charCodeAt(++i);
      u = 0x10000 + ((u & 0x3FF) << 10) | u1 & 0x3FF;
    }

    if (u <= 0x7F) {
      if (outIdx >= endIdx) {
        break;
      }

      heap[outIdx++] = u;
    } else if (u <= 0x7FF) {
      if (outIdx + 1 >= endIdx) {
        break;
      }

      heap[outIdx++] = 0xC0 | u >> 6;
      heap[outIdx++] = 0x80 | u & 63;
    } else if (u <= 0xFFFF) {
      if (outIdx + 2 >= endIdx) {
        break;
      }

      heap[outIdx++] = 0xE0 | u >> 12;
      heap[outIdx++] = 0x80 | u >> 6 & 63;
      heap[outIdx++] = 0x80 | u & 63;
    } else {
      if (outIdx + 3 >= endIdx) {
        break;
      }

      if (u > 0x10FFFF) {
        warnOnce(`Invalid Unicode code point ${ptrToString(u)} encountered when serializing a JS string to a UTF-8 string in wasm memory! (Valid unicode code points should be in range 0-0x10FFFF).`);
      }

      heap[outIdx++] = 0xF0 | u >> 18;
      heap[outIdx++] = 0x80 | u >> 12 & 63;
      heap[outIdx++] = 0x80 | u >> 6 & 63;
      heap[outIdx++] = 0x80 | u & 63;
    }
  }

  heap[outIdx] = 0; // null-terminate the pointer to the buffer.
  return outIdx - startIdx;
}

export function isLittleEndian() {
  const h16 = new Int16Array(1);
  const h8 = new Int8Array(h16.buffer);

  h16[0] = 0x6373;
  return !(h8[0] !== 0x73 || h8[1] !== 0x63);
}
